import { cn } from '../../lib/cn.ts';

export interface StatusTab {
  /** Stable tab id, used for the value and the per-tab testid suffix. */
  id: string;
  /** Visible tab label. */
  label: string;
}

export interface StatusTabsProps {
  /** The tabs to render, left to right. */
  tabs: StatusTab[];
  /** The id of the active tab. */
  value: string;
  /** Called with the tab id when a tab is activated. */
  onChange: (id: string) => void;
  className?: string;
  'data-testid'?: string;
}

/**
 * StatusTabs — the prototype `.tabs` bar: a horizontal row of tab buttons on a
 * bottom hairline (`--color-line`). The active tab takes `--color-charcoal` ink
 * plus a 2px `--color-accent-deep` bottom border (sitting on the hairline via a
 * -1px margin); inactive tabs are `--color-stone-mid` and darken on hover.
 *
 * Each tab is a button with testid `${data-testid}-${tab.id}`.
 */
export function StatusTabs({
  tabs,
  value,
  onChange,
  className,
  'data-testid': testId,
}: StatusTabsProps) {
  return (
    <div
      role="tablist"
      data-testid={testId}
      className={cn('flex gap-1 border-b border-[var(--color-line)]', className)}
    >
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={testId ? `${testId}-${tab.id}` : undefined}
            onClick={() => onChange(tab.id)}
            className={cn(
              '-mb-px border-b-2 border-transparent px-1 py-2.5 text-[13.5px] font-medium transition-colors',
              active
                ? 'border-[var(--color-accent-deep)] text-[var(--color-charcoal)]'
                : 'text-[var(--color-stone-mid)] hover:text-[var(--color-charcoal)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
